/**
 * Statement Results Component
 * Display and download generated statements
 */

import React, { useState } from 'react';
import { generationAPI } from '../../services/api';

interface StatementResultsProps {
  result: any; // MultiMonthStatementResult from API
  onReset?: () => void;
}

export const StatementResults: React.FC<StatementResultsProps> = ({ result, onReset }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async (monthIndex: number) => {
    try {
      setDownloading(true);
      const blob = await generationAPI.downloadPDF(monthIndex, result);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthData = result.months[monthIndex];
      const monthName = new Date(monthData.periodStart).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      link.download = `statement_${monthName.replace(' ', '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewPDF = async (monthIndex: number) => {
    try {
      const blob = await generationAPI.downloadPDF(monthIndex, result);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Preview failed:', err);
      alert('Failed to preview PDF');
    }
  };

  const handlePreviewHTML = (monthIndex: number) => {
    const htmlContent = result.htmlPerMonth[monthIndex];
    if (htmlContent) {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(htmlContent);
        win.document.close();
      }
    }
  };

  const handleDownloadExcel = async (monthIndex: number) => {
    try {
      setDownloading(true);
      const base64Data = result.excelPerMonth[monthIndex];
      
      // Convert base64 to blob
      const binaryString = window.atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const monthData = result.months[monthIndex];
      const monthName = new Date(monthData.periodStart).toLocaleDateString("en-US", { year: "numeric", month: "long" });
      link.download = `statement_${monthName.replace(" ", "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to download Excel file");
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!result || !result.months) {
    return <div className="text-red-600">Error: Invalid result data</div>;
  }

  // Check for PDF availability - either old format (pdfPerMonth) or new format (months[].pdfUrl)
  const hasPDF = (result.pdfPerMonth && Object.keys(result.pdfPerMonth).length > 0) || 
                 (result.months && result.months.some((m: any) => m.pdfUrl));
  const hasHTML = result.htmlPerMonth && Object.keys(result.htmlPerMonth).length > 0;
  const hasExcel = result.excelPerMonth && Object.keys(result.excelPerMonth).length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <svg
            className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-900 mb-1">
              Statement Generated Successfully!
            </h3>
            <p className="text-green-700 text-sm">
              Generated {result.months.length} month{result.months.length > 1 ? 's' : ''} for {result.accountHolderName}
            </p>
          </div>
        </div>

        {onReset && (
          <div className="mt-4">
            <button
              onClick={onReset}
              className="text-green-700 hover:text-green-800 underline text-sm"
            >
              Generate Another Statement
            </button>
          </div>
        )}
      </div>

      {/* Statement Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Statement Summary</h3>
          <p className="text-sm text-gray-600 mt-1">
            {result.bankName} • Account: {result.accountNumberMasked}
          </p>
        </div>

        <div className="divide-y divide-gray-200">
          {result.months.map((statement: any, index: number) => (
            <div key={index} className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">
                  {formatDate(statement.periodStart)} - {formatDate(statement.periodEnd)}
                </h4>
                <span className="text-sm text-gray-500">
                  {statement.transactions?.length || 0} transaction{statement.transactions?.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Starting Balance</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(statement.openingBalance)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Ending Balance</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(statement.closingBalance)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Deposits</p>
                  <p className="text-sm font-semibold text-green-600">
                    +{formatCurrency(statement.totals?.totalCredits || 0)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Total Withdrawals</p>
                  <p className="text-sm font-semibold text-red-600">
                    -{formatCurrency(Math.abs(statement.totals?.totalDebits || 0))}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                {hasPDF && (
                  <>
                    <button
                      onClick={() => handleDownloadPDF(index)}
                      disabled={downloading}
                      className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 text-sm"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      Download PDF
                    </button>

                    <button
                      onClick={() => handlePreviewPDF(index)}
                      className="flex items-center gap-2 bg-white text-blue-700 border border-blue-300 px-3 py-2 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      Preview PDF
                    </button>
                  </>
                )}

                {hasHTML && (
                  <button
                    onClick={() => handlePreviewHTML(index)}
                    className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    </svg>
                    View HTML
                  </button>
                )}
              

          {/* Excel Buttons */}
          {hasExcel && (
            <div className="flex gap-2">
              <button
                onClick={() => handleDownloadExcel(index)}
                disabled={downloading}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 text-sm"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Excel
              </button>
            </div>
          )}
        </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg
            className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Mock Statement - For Testing Only</p>
            <p className="text-blue-700">
              This statement contains synthetic data generated for testing purposes.
              All transactions, names, and account information are fictitious.
              The document includes a "SAMPLE" watermark to prevent misuse.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
